import React, { useState, useEffect } from 'react';
import { Shield, AlertTriangle, CheckCircle, Users, Calculator, Loader2, TrendingUp, AlertCircle } from 'lucide-react';
import { feeCollectionAPI, securitfeeAPI, formatCurrency, formatNumber } from '../services/api';

const SecurityFeeAudit = ({ appliedMonth, appliedYear, searchTrigger = 0 }) => {
  const [selectedPeriod, setSelectedPeriod] = useState('current');
  const [auditData, setAuditData] = useState(null);
  const [eligibilityData, setEligibilityData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [eligibilityLoading, setEligibilityLoading] = useState(true);
  const [error, setError] = useState(null);
  const [eligibilityError, setEligibilityError] = useState(null);

  // UI state for Eligibility by Operator section (reserved for future sorting/expand)
  const [opSort, setOpSort] = useState({ key: 'rate', dir: 'desc' });
  const [expandedOps, setExpandedOps] = useState({});
  void opSort;
  void setOpSort;
  void expandedOps;

  // Fetch audit data from API; refetch when parent Search changes applied month/year
  useEffect(() => {
    const fetchAuditData = async () => {
      try {
        setLoading(true);
        const auditParams = {};
        if (appliedMonth != null && appliedMonth !== '') auditParams.month = appliedMonth;
        if (appliedYear != null && appliedYear !== '') auditParams.year = appliedYear;
        const response = await feeCollectionAPI.getAudit(auditParams);
        const raw = response?.data ?? response;
        const success = response?.success ?? !!raw;
        if (success && raw) {
          setAuditData(typeof raw === 'object' && !Array.isArray(raw) ? raw : {});
          setError(null);
        } else {
          setError('Failed to fetch audit data');
        }
      } catch (err) {
        console.error('Error fetching audit data:', err);
        setError('Failed to load security fee audit data');
      } finally {
        setLoading(false);
      }
    };

    const fetchEligibilityData = async () => {
      try {
        setEligibilityLoading(true);
        setEligibilityError(null);
        const params = { period: selectedPeriod };
        if (appliedMonth != null && appliedMonth !== '') params.month = appliedMonth;
        if (appliedYear != null && appliedYear !== '') params.year = appliedYear;

        let payload = null;
        try {
          const response = await feeCollectionAPI.getSubscriberEligibility(params);
          const raw = response?.data ?? response;
          const success = response?.success ?? !!raw;
          if (success && raw && typeof raw === 'object') {
            payload = raw;
          }
        } catch (e) {
          console.warn('Subscriber eligibility API error:', e?.message);
        }

        // Normalize: support serviceSummary / summary in different places
        const serviceSummary = payload?.serviceSummary ?? payload?.byService ?? payload?.services;
        const summary = payload?.summary ?? payload?.eligibilitySummary ?? payload?.totals;
        const hasSummary = summary && (Number(summary.totalActive ?? 0) + Number(summary.totalEligible ?? 0) + Number(summary.totalNotEligible ?? 0) > 0);
        const hasServiceSummary = Array.isArray(serviceSummary) && serviceSummary.length > 0;

        if (hasSummary || hasServiceSummary) {
          const normalizedServiceSummary = Array.isArray(serviceSummary) ? serviceSummary.map((s) => ({
            subscriberType: s.subscriberType ?? s.subscriber_type ?? s.serviceType ?? s.service_type ?? s.name,
            activeSubscribers: Number(s.activeSubscribers ?? s.active_subscribers ?? 0),
            totalSubscribers: Number(s.totalSubscribers ?? s.total_subscribers ?? 0),
            eligibleSubscribers: Number(s.eligibleSubscribers ?? s.eligible_subscribers ?? 0),
            notEligibleSubscribers: Number(s.notEligibleSubscribers ?? s.not_eligible_subscribers ?? 0)
          })) : [];
          setEligibilityData({
            serviceSummary: normalizedServiceSummary,
            summary: summary ? {
              totalActive: Number(summary.totalActive ?? summary.total_active ?? 0),
              totalEligible: Number(summary.totalEligible ?? summary.total_eligible ?? 0),
              totalNotEligible: Number(summary.totalNotEligible ?? summary.total_not_eligible ?? 0),
              overallEligibilityRate: Number(summary.overallEligibilityRate ?? summary.overall_eligibility_rate ?? 0)
            } : undefined
          });
          setEligibilityLoading(false);
          return;
        }

        // Fallback: build from overview + by-operator (same period as rest of dashboard)
        const periodParams = {};
        if (appliedMonth != null && appliedMonth !== '') periodParams.month = appliedMonth;
        if (appliedYear != null && appliedYear !== '') periodParams.year = appliedYear;
        try {
          const [overviewRes, byOpRes] = await Promise.all([
            securitfeeAPI.getOverview(periodParams).catch(() => null),
            securitfeeAPI.getByOperator(periodParams).catch(() => null)
          ]);
          const rawByOp = Array.isArray(byOpRes) ? byOpRes : (byOpRes?.data ?? []);
          const totalEligible = rawByOp.reduce((sum, op) => sum + (Number(op.eligibleSubscribers ?? op.eligible ?? 0) || 0), 0);
          const overview = overviewRes?.data ?? overviewRes ?? {};
          const ov = overview?.overview ?? overview;
          const totalCollected = Number(ov?.totalCollected ?? overview?.totalCollected ?? 0);
          const remaining = Number(ov?.remaining ?? overview?.remaining ?? 0);
          const totalExpected = Number(ov?.totalEstimate ?? overview?.totalEstimate ?? 0) || (totalCollected + remaining);
          if (totalEligible > 0 || totalExpected > 0) {
            const totalActive = totalEligible;
            setEligibilityData({
              serviceSummary: rawByOp.map((op) => ({
                subscriberType: op.operatorName ?? op.operator ?? 'Operator',
                activeSubscribers: Number(op.eligibleSubscribers ?? op.eligible ?? 0),
                eligibleSubscribers: Number(op.eligibleSubscribers ?? op.eligible ?? 0),
                notEligibleSubscribers: 0
              })),
              summary: {
                totalActive,
                totalEligible,
                totalNotEligible: 0,
                overallEligibilityRate: totalActive > 0 ? 100 : 0
              }
            });
          } else {
            setEligibilityData(null);
          }
        } catch (e) {
          console.warn('Eligibility fallback (overview/by-operator) error:', e?.message);
          setEligibilityData(null);
        }
      } catch (err) {
        console.error('Error fetching eligibility data:', err);
        setEligibilityError('Failed to load subscriber eligibility data');
        setEligibilityData(null);
      } finally {
        setEligibilityLoading(false);
      }
    };

    fetchAuditData();
    fetchEligibilityData();
  }, [selectedPeriod, appliedMonth, appliedYear, searchTrigger]);

  // Show loading spinner
  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600 mr-3" />
        <span className="text-lg text-gray-600">Loading security fee audit data...</span>
      </div>
    );
  }

  // Show error message
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-red-800 mb-2">Error Loading Data</h3>
        <p className="text-red-600">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  // Extract data from API response (safe defaults for alignment with various API shapes)
  const feeRates = auditData?.feeRates ?? {};
  const serviceData = auditData?.serviceData ?? {};
  const collectedFees = auditData?.collectedFees ?? {};
  const totals = auditData?.totals ?? { expected: 0, collected: 0, collectionRate: 0 };
  const compliance = auditData?.compliance ?? {};

  // Use API's formatCurrency function for consistent formatting
  const formatKip = (amount) => {
    return formatCurrency(amount);
  };

  // Use API's formatNumber function
  const formatNumbers = (num) => {
    return formatNumber(num);
  };

  // Helper methods for expand/collapse (for future operator row expand)
  const toggleOperator = (key) => {
    setExpandedOps(prev => ({ ...prev, [key]: !prev[key] }));
  };
  const setAllExpanded = (keys, value) => {
    const next = {};
    keys.forEach(k => { next[k] = value; });
    setExpandedOps(next);
  };
  void toggleOperator;
  void setAllExpanded;
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <Shield className="h-6 w-6 text-blue-600 mr-3" />
          <div>
            <h2 className="text-xl font-bold text-gray-900">Security Fee Audit</h2>
            <p className="text-gray-600">Fee collection audit based on active subscribers</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <label className="text-sm text-gray-600">Period:</label>
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="current">Current Month</option>
            <option value="previous">Previous Month</option>
            <option value="quarterly">Quarterly</option>
          </select>
        </div>
      </div>

      {/* Fee Rate Information */}
      <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg">
        <div className="flex items-center mb-2">
          <Calculator className="h-5 w-5 text-blue-600 mr-2" />
          <h3 className="text-lg font-semibold text-blue-900">Security Fee Rates</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white rounded-lg p-3">
            <div className="text-sm text-gray-600">MBB (Mobile Broadband)</div>
            <div className="text-xl font-bold text-blue-600">{formatKip(feeRates?.MBB ?? 0)}</div>
            <div className="text-xs text-gray-500">per active subscriber/month</div>
          </div>
          <div className="bg-white rounded-lg p-3">
            <div className="text-sm text-gray-600">FTB (Fixed/Fiber)</div>
            <div className="text-xl font-bold text-green-600">{formatKip(feeRates?.FTB ?? 0)}</div>
            <div className="text-xs text-gray-500">per active subscriber/month</div>
          </div>
        </div>
      </div>
      {/* Eligibility Summary Section */}
      {eligibilityLoading ? (
        <div className="flex items-center justify-center p-6 bg-gray-50 rounded-lg mb-6">
          <Loader2 className="h-6 w-6 animate-spin text-blue-500 mr-2" />
          <span className="text-gray-600">Loading eligibility metrics...</span>
        </div>
      ) : eligibilityError ? (
        <div className="bg-red-50 border border-red-100 p-4 rounded-lg mb-6 flex items-center">
          <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
          <span className="text-red-700">{eligibilityError}</span>
        </div>
      ) : eligibilityData ? (
        <React.Fragment>
          {(() => {
            const svc = eligibilityData.serviceSummary || [];
            const mbb = svc.find(s => s.subscriberType === 'MBB');
            const ftb = svc.find(s => s.subscriberType === 'FTB');
            const activeMBB = mbb?.activeSubscribers || 0;
            const activeFTB = ftb?.activeSubscribers || 0;
            const _totalMBB = mbb?.totalSubscribers || 0;
            const _totalFTB = ftb?.totalSubscribers || 0;
            void _totalMBB;
            void _totalFTB;
            const totalActiveCalc = activeMBB + activeFTB;
            const totalEligibleCalc = svc.reduce((sum, s) => sum + (s.eligibleSubscribers || 0), 0);
            const totalNotEligibleCalc = svc.reduce((sum, s) => sum + (s.notEligibleSubscribers || 0), 0);
            const rateCalc = totalActiveCalc > 0 ? (totalEligibleCalc / totalActiveCalc) * 100 : 0;

            const hasApiSummary = !!eligibilityData.summary && ((eligibilityData.summary.totalActive || 0) + (eligibilityData.summary.totalEligible || 0) + (eligibilityData.summary.totalNotEligible || 0)) > 0;
            const summary = hasApiSummary ? eligibilityData.summary : {
              totalActive: totalActiveCalc,
              totalEligible: totalEligibleCalc,
              totalNotEligible: totalNotEligibleCalc,
              overallEligibilityRate: parseFloat(rateCalc.toFixed(1))
            };

            return (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-6 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-blue-100 text-sm">Total Active Subscribers</p>
                      <p className="text-2xl font-bold">{formatNumbers(summary.totalActive)}</p>
                      <p className="text-blue-100 text-xs mt-1">MBB + FTB subscribers</p>
                    </div>
                    <Users className="h-8 w-8 text-blue-200" />
                  </div>
                </div>

                <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg p-6 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-green-100 text-sm">Eligible Subscribers</p>
                      <p className="text-2xl font-bold">{formatNumbers(summary.totalEligible)}</p>
                      <p className="text-green-100 text-xs mt-1">MBB ≥ 3,000 ₭; FTB ≥ 5,000 ₭</p>
                    </div>
                    <CheckCircle className="h-8 w-8 text-green-200" />
                  </div>
                </div>

                <div className="bg-gradient-to-r from-red-500 to-red-600 rounded-lg p-6 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-red-100 text-sm">Cannot Deduct (Not Eligible)</p>
                      <p className="text-2xl font-bold">{formatNumbers(summary.totalNotEligible)}</p>
                      <p className="text-red-100 text-xs mt-1">Balance below threshold</p>
                    </div>
                    <AlertTriangle className="h-8 w-8 text-red-200" />
                  </div>
                </div>

                <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg p-6 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-purple-100 text-sm">Eligibility Rate</p>
                      <p className="text-2xl font-bold">{(summary.overallEligibilityRate || 0).toFixed(1)}%</p>
                      <p className="text-purple-100 text-xs mt-1">Overall eligibility</p>
                    </div>
                    <TrendingUp className="h-8 w-8 text-purple-200" />
                  </div>
                </div>
              </div>
            );
          })()}
        </React.Fragment>
      ) : null}


      {/* Service-specific Audit */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {['MBB', 'FTB'].map((service) => {
          const complianceInfo = compliance[service] ?? {};
          const data = serviceData[service] ?? {};
          const fees = collectedFees[service] ?? {};
          const expected = fees?.expected ?? 0;
          const collected = fees?.collected ?? 0;
          const collectionRate = expected > 0 ? (collected / expected) * 100 : 0;

          return (
            <div key={service} className="bg-white rounded-lg shadow-md p-6 border">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">{service} Security Fee Audit</h3>
                  <p className="text-sm text-gray-600">
                    {service === 'MBB' ? 'Mobile Broadband Services' : 'Fixed/Fiber Services'}
                  </p>
                </div>
                <div className={`px-3 py-1 rounded-full text-xs font-medium bg-${complianceInfo?.color ?? 'gray'}-100 text-${complianceInfo?.color ?? 'gray'}-800`}>
                  {complianceInfo?.text ?? '-'}
                </div>
              </div>

              {/* Subscriber Breakdown */}
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Subscriber Status</h4>
                <div className="grid grid-cols-3 gap-3">
                  <div className="text-center p-3 bg-blue-50 rounded-lg">
                    <div className="text-lg font-bold text-blue-600">
                      {formatNumbers(data?.totalSubscribers || 0)}
                    </div>
                    <div className="text-xs text-gray-600">Total</div>
                  </div>
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <div className="text-lg font-bold text-green-600">
                      {formatNumbers(data?.activeSubscribers || 0)}
                    </div>
                    <div className="text-xs text-gray-600">Active ({data?.activeRate || 0}%)</div>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <div className="text-lg font-bold text-gray-600">
                      {formatNumbers(data?.inactiveSubscribers || 0)}
                    </div>
                    <div className="text-xs text-gray-600">Inactive</div>
                  </div>
                </div>
              </div>

              {/* Fee Calculation */}
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Fee Calculation</h4>
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Active Subscribers:</span>
                    <span className="font-medium">{formatNumbers(data?.activeSubscribers || 0)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Rate per subscriber:</span>
                    <span className="font-medium">{formatKip(feeRates?.[service] ?? 0)}</span>
                  </div>
                  <div className="border-t pt-2 flex justify-between font-semibold">
                    <span>Expected Collection:</span>
                    <span className="text-blue-600">{formatKip(fees?.expected || 0)}</span>
                  </div>
                </div>
              </div>

              {/* Collection Status */}
              <div className="mb-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Collection Status</h4>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Collected</span>
                    <div className="text-right">
                      <div className="text-sm font-medium text-green-600">{formatKip(fees?.collected || 0)}</div>
                      <div className="text-xs text-gray-500">{collectionRate.toFixed(1)}%</div>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Pending</span>
                    <div className="text-right">
                      <div className="text-sm font-medium text-yellow-600">{formatKip(fees?.pending || 0)}</div>
                      <div className="text-xs text-gray-500">{fees?.expected > 0 ? ((fees.pending / fees.expected) * 100).toFixed(1) : 0}%</div>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Overdue</span>
                    <div className="text-right">
                      <div className="text-sm font-medium text-red-600">{formatKip(fees?.overdue || 0)}</div>
                      <div className="text-xs text-gray-500">{fees?.expected > 0 ? ((fees.overdue / fees.expected) * 100).toFixed(1) : 0}%</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="mb-4">
                <div className="flex justify-between text-xs text-gray-600 mb-1">
                  <span>Collection Progress</span>
                  <span>{collectionRate.toFixed(1)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div className="flex h-3 rounded-full overflow-hidden">
                    <div
                      className="bg-green-500 transition-all duration-500"
                      style={{ width: `${fees?.expected > 0 ? (fees.collected / fees.expected) * 100 : 0}%` }}
                    ></div>
                    <div
                      className="bg-yellow-400 transition-all duration-500"
                      style={{ width: `${fees?.expected > 0 ? (fees.pending / fees.expected) * 100 : 0}%` }}
                    ></div>
                    <div
                      className="bg-red-500 transition-all duration-500"
                      style={{ width: `${fees?.expected > 0 ? (fees.overdue / fees.expected) * 100 : 0}%` }}
                    ></div>
                  </div>
                </div>
              </div>

              {/* Action Items */}
              {collectionRate < 95 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <div className="flex items-start">
                    <AlertTriangle className="h-4 w-4 text-yellow-600 mr-2 mt-0.5" />
                    <div>
                      <div className="text-xs font-medium text-yellow-800">Action Required</div>
                      <div className="text-xs text-yellow-700 mt-1">
                        Collection rate is below target (95%). Review outstanding fees and follow up on pending collections.
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Comparison Summary */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Collection Comparison Summary</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Service</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Active Subscribers</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rate (Kip)</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expected</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Collected</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Gap</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {['MBB', 'FTB'].map((service) => {
                const data = serviceData[service] ?? {};
                const fees = collectedFees[service] ?? {};
                const gap = (fees?.expected || 0) - (fees?.collected || 0);
                const complianceInfo = compliance[service] ?? {};

                return (
                  <tr key={service}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{service}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatNumbers(data?.activeSubscribers || 0)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatKip(feeRates?.[service] ?? 0)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatKip(fees?.expected || 0)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-medium">
                      {formatKip(fees?.collected || 0)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600">
                      {formatKip(gap)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-${complianceInfo?.color ?? 'gray'}-100 text-${complianceInfo?.color ?? 'gray'}-800`}>
                        {complianceInfo?.text ?? '-'}
                      </span>
                    </td>
                  </tr>
                );
              })}
              <tr className="bg-gray-50 font-medium">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">Total</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {formatNumbers((serviceData?.MBB?.activeSubscribers || 0) + (serviceData?.FTB?.activeSubscribers || 0))}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">-</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {formatKip(totals?.expected ?? 0)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                  {formatKip(totals?.collected ?? 0)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600">
                  {formatKip((totals?.expected ?? 0) - (totals?.collected ?? 0))}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-sm text-gray-600">
                    {totals?.collectionRate ?? 0}%
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SecurityFeeAudit;
